import React, { useState, useRef, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { DashboardLayout } from "../components/layout";
import PostCard from "../components/feed/PostCard";
import Icon from "../components/common/Icon";
import api from "../services/api";
import ASSETS from "../data/assets";
import {
  Badgge1,
  Badgge2,
  Badgge3,
  Badgge4,
  Badgge5,
  Badgge6,
  CrownIcon,
  PremiumIcon,
} from "../assets/images/image";
import CreatePostModal from "../components/post/CreatePostModal";
import { Pencil } from "lucide-react";
import EditTagsModal from "../components/modals/EditTagsModal";
import { getAssetUrl } from "../services/helper";

/**
 * NEW api.js methods required (add these to services/api.js if not present):
 *
 *   getMe: () => request("GET", "/user/me")
 *     -> resolves to: { user: { id, name, email, avatar, points, about_us,
 *        location, role, user_role, interests, badge, member_since, stats, subscription } }
 *
 *   updateUserRole: (payload) => request("PATCH", "/user/role", payload)
 *     -> payload: { user_role: "mentor" | "mentee" | "both" }
 *
 *   updateInterests: (payload) => request("PATCH", "/user/interests", payload)
 *     -> payload: { interests: string[] }
 */

const avSrc = (p) =>
  !p
    ? "/assets/images/avatar-brother-julian.png"
    : p.startsWith("/uploads")
      ? `${process.env.REACT_APP_API_URL || "http://localhost:5000/api"}${p}`
      : p;

const parseInterests = (raw) => {
  if (Array.isArray(raw)) return raw;
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};
const API_ROOT = (process.env.REACT_APP_API_URL || "http://localhost:5000/api").replace("/api", "");

// ─── Edit Profile Modal ─────────────────────────────────────────────────────
const EditProfileModal = ({ isOpen, onClose, user, onSaved, isSaving, error }) => {
  const [form, setForm] = useState({ name: "", about_us: "" });
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const fileRef = useRef();

  // Sync form when modal opens with fresh user data
  React.useEffect(() => {
    if (isOpen && user) {
      setForm({
        name: user.name || "",
        about_us: user.about_us || "",
      });
      setAvatarFile(null);
      setAvatarPreview(null);
    }
  }, [isOpen, user]);

  if (!isOpen) return null;

  const handleAvatar = (e) => {
    const f = e.target.files[0];
    if (f) {
      setAvatarFile(f);
      setAvatarPreview(URL.createObjectURL(f));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const fd = new FormData();
    if (form.name) fd.append("name", form.name);
    fd.append("about_us", form.about_us);
    if (avatarFile) fd.append("avatar", avatarFile);
    onSaved(fd);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-md" onClick={onClose} />

      <div
        className="relative bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden"
        style={{ width: "644px" }}
      >
        <div className="flex items-center justify-between px-6 pt-5 pb-3 flex-shrink-0">
          <h2 className="text-[22px] font-medium text-gray-900 font-body">Edit Profile</h2>
          <button onClick={onClose} className="flex items-center justify-center rounded-full transition-colors">
            <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#6B7280" strokeWidth="2" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6">
          <div className="flex flex-col mb-6">
            <label className="text-[17.09px] font-medium text-gray-700 mb-3 font-body">
              Profile Picture
            </label>

            <div className="relative cursor-pointer group w-fit" onClick={() => fileRef.current?.click()}>
              <div
                className="rounded-full p-[6px]"
                style={{ backgroundColor: "#1F3A60", boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }}
              >
                <div className="w-[114px] h-[114px] rounded-full overflow-hidden">
                  <img
                    src={avatarPreview || avSrc(user?.avatar)}
                    alt="Profile"
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.currentTarget.src = "/assets/images/avatar-brother-julian.png";
                    }}
                  />
                </div>
              </div>

              <div
                className="absolute bottom-1 right-0 rounded-full flex items-center justify-center z-10"
                style={{ width: "38.22px", height: "38.22px", backgroundColor: "#1F3A60", boxShadow: "0 2px 6px rgba(0,0,0,0.15)" }}
              >
                <Pencil size={14} color="white" />
              </div>
            </div>

            <input type="file" ref={fileRef} accept="image/*" onChange={handleAvatar} className="hidden" />
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600">
              {error}
            </div>
          )}

          <div className="mb-5">
            <label className="block text-[17.09px] font-medium text-gray-700 mb-2 font-body">Name</label>
            <input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Your name"
              className="w-full px-4 py-3 border rounded-[10px] text-[14px] font-normal outline-none transition-all font-body bg-[#F9F9F9] border-[#dadada] focus:border-[#1F3A60] focus:bg-white hover:bg-white"
              style={{ borderRadius: "10px", height: "60px" }}
            />
          </div>

          <div>
            <label className="block text-[17.09px] font-medium text-gray-700 mb-2 font-body">About</label>
            <textarea
              value={form.about_us}
              onChange={(e) => setForm({ ...form, about_us: e.target.value })}
              placeholder="Write about yourself..."
              rows={4}
              className="w-full px-4 py-3 border rounded-[10px] text-[14px] font-normal outline-none transition-all font-body bg-[#F9F9F9] border-[#dadada] focus:border-[#1F3A60] focus:bg-white hover:bg-white resize-none"
              style={{ borderRadius: "10px" }}
            />
          </div>
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-6 py-2.5 bg-gray-200 text-gray-700 rounded-[10px] text-[16px] font-normal hover:bg-gray-300 transition-all font-body"
          >
            Cancel
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            disabled={isSaving}
            className="px-6 py-2.5 bg-[#1F3A60] text-white rounded-[10px] text-[16px] font-normal hover:bg-[#2a4a7a] disabled:opacity-60 transition-all font-body"
          >
            {isSaving ? (
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Saving...
              </div>
            ) : (
              "Save Changes"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── ProfilePage ──────────────────────────────────────────────────────────────
const ProfilePage = () => {
  const queryClient = useQueryClient();
  const [showEdit, setShowEdit] = useState(false);
  const [postPage, setPostPage] = useState(1);
  const [streamPageNo, setStreamPageNo] = useState(1);

  const [allPosts, setAllPosts] = useState([]); // Accumulate posts  
  const [allSavedStreams, setAllSavedStreams] = useState([]); // Accumulate saved streams

  const [selectedMedia, setSelectedMedia] = useState(null);

  const [showCreate, setShowCreate] = useState(false);
  const [showEditTags, setShowEditTags] = useState(false);
  const [activeTab, setActiveTab] = useState("feed");

  const MediaModal = ({ item, onClose }) => {

    return (
      <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4" onClick={onClose}>
        <div className="bg-black rounded-2xl overflow-hidden max-w-4xl w-full relative" onClick={e => e.stopPropagation()}>
          <button
            onClick={onClose}
            className="absolute top-4 right-4 z-10 bg-white/20 hover:bg-white/40 text-white rounded-full px-4 py-2 text-sm"
          >
            ✕ Close
          </button>

          <MediaRenderer url={item.media_url} title={item.title} apiRoot={API_ROOT} />

          <div className="p-5 bg-white text-black">
            <h3 className="font-bold text-xl">{item.title}</h3>
            <p className="text-gray-600 mt-1">{item.author}</p>
          </div>
        </div>
      </div>
    );
  };

  const MediaRenderer = ({ url, title }) => {
    const fileType = getFileType(url);
    const mediaSrc = url?.startsWith("/uploads") ? `${API_ROOT}${url}` : url;

    switch (fileType) {
      case 'video':
        return <video src={mediaSrc} controls className="w-full max-h-[80vh] bg-black rounded-xl" />;
      case 'audio':
        return <audio src={mediaSrc} controls className="w-full" />;
      case 'image':
        return <img src={mediaSrc} alt={title} className="w-full max-h-[80vh] object-contain" />;
      default:
        return <div className="p-8 text-white text-center">Unsupported media</div>;
    }
  };
  const getFileType = (url) => {
    const ext = url?.toLowerCase().substring(url.lastIndexOf('.'));
    if (['.mp4', '.webm', '.mov'].includes(ext)) return 'video';
    if (['.mp3', '.wav', '.m4a'].includes(ext)) return 'audio';
    if (['.jpg', '.jpeg', '.png', '.gif'].includes(ext)) return 'image';
    return 'unknown';
  };
  const isVideoFile = (url) => getFileType(url) === 'video';

  // ── Fetch current user (getMe) ──────────────────────────────────────────
  const {
    data: meData,
    isLoading: isLoadingMe,
  } = useQuery({
    queryKey: ["auth-me"],
    queryFn: () => api.getMe(),
    staleTime: 60 * 1000,
  });

  console.log({ user_data: meData?.user });
  const

    p = meData?.user || {};
  const stats = p.stats || {};
  const subscription = p.subscription || {};
  const tags = parseInterests(p.interests);

  // ── Fetch this user's posts ─────────────────────────────────────────────
  // Fetch Posts with Pagination
  const {
    data: postsPageData,
    isLoading: isLoadingPosts,
    isFetching: isFetchingMore
  } = useQuery({
    queryKey: ["my-posts", postPage],
    queryFn: () => api.getMyPost(postPage),
    enabled: !!p.id && activeTab === "feed",
    keepPreviousData: true,
  });

  const newPosts = postsPageData?.posts || [];

  // Append new page to accumulated posts
  useEffect(() => {
    if (newPosts.length > 0) {
      setAllPosts(prev => {
        const existingIds = new Set(prev.map(p => p.id));
        const filteredNew = newPosts.filter(post => !existingIds.has(post.id));
        return [...prev, ...filteredNew];
      });
    }
  }, [newPosts]);

  const hasMore = postsPageData?.pagination?.hasNext || false;

  const loadMorePosts = () => {
    if (!hasMore || isFetchingMore) return;
    setPostPage(prev => prev + 1);
  };

  // Reset when switching tabs
  // Reset when switching tabs
  useEffect(() => {
    if (activeTab === "feed") {
      setStreamPageNo(1);
      // setAllSavedStreams([]);
    } else {
      setPostPage(1);
      // setAllPosts([]);
    }
  }, [activeTab]);

  // ── Mutation: update profile (name / about_us / avatar) ────────────────
  const updateProfileMutation = useMutation({
    mutationFn: (fd) => api.updateProfile(fd),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["auth-me"] });
      setShowEdit(false);
    },
  });

  // ── Mutation: update role (mentor / mentee / both) ──────────────────────
  const updateRoleMutation = useMutation({
    mutationFn: (user_role) => api.updateUserRole(user_role),
    onMutate: async (user_role) => {
      await queryClient.cancelQueries({ queryKey: ["auth-me"] });
      const previous = queryClient.getQueryData(["auth-me"]);
      queryClient.setQueryData(["auth-me"], (old) =>
        old ? { ...old, user: { ...old.user, user_role } } : old
      );
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(["auth-me"], context.previous);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["auth-me"] });
    },
  });

  // ── Mutation: update interest tags ──────────────────────────────────────
  const updateInterestsMutation = useMutation({
    mutationFn: (interests) => api.updateUserInterest({ interest_tags: interests }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["auth-me"] });
      setShowEditTags(false);
    },
  });

  const handleRoleChange = (value) => {
    if (value === p.user_role || updateRoleMutation.isPending) return;
    updateRoleMutation.mutate(value);
  };

  const handleTagsSaved = (newTags) => {
    updateInterestsMutation.mutate(newTags);
  };
  // Saved Streams
  // Saved Streams
  const { data: savedData, isLoading: isLoadingSaved, isFetching: isFetchingMoreStreams } = useQuery({
    queryKey: ["saved-streams", streamPageNo],
    queryFn: () => api.getSavedStreamMedia(streamPageNo, 9),
    enabled: activeTab === "saved",
    keepPreviousData: true,
  });

  const newSavedStreams = savedData?.saved || [];

  // Append new page to accumulated saved streams
  useEffect(() => {
    if (newSavedStreams.length > 0) {
      setAllSavedStreams(prev => {
        const existingIds = new Set(prev.map(s => s.id));
        const filteredNew = newSavedStreams.filter(item => !existingIds.has(item.id));
        return [...prev, ...filteredNew];
      });
    }
  }, [newSavedStreams]);

  const hasMoreStreams = savedData?.pagination?.hasNext || false;

  const loadMoreStreams = () => {
    if (!hasMoreStreams || isFetchingMoreStreams) return;
    setStreamPageNo(prev => prev + 1);
  };
  // Toggle Save Mutation (for saved tab)
  const toggleSaveMutation = useMutation({
    mutationFn: (mediaId) => api.toggleSaveStreamMedia(mediaId),
    onSuccess: (response, mediaId) => {        // mediaId is passed from mutate()
      setAllSavedStreams((prev) =>
        prev.filter((item) => item.id !== mediaId)
      );
      queryClient.invalidateQueries({ queryKey: ["saved-streams"] });
    },
  });

  const handleSaveToggle = (item) => {
    toggleSaveMutation.mutate(item.id);
  };
  if (isLoadingMe) {
    return (
      <DashboardLayout onCreatePost={() => setShowCreate(true)}>
        <div className="flex-1 flex items-center justify-center">
          <div className="w-8 h-8 border-3 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout onCreatePost={() => setShowCreate(true)}>
      <div
        className="flex-1 min-w-0 overflow-y-auto scrollbar-thin px-4 md:px-6 pt-4 sm:pt-6 pb-8"
        style={{ maxHeight: "calc(100vh - 56px)" }}
      >
        {/* ── CARD 1: Cover + Profile Header ── */}
        <div className="bg-white rounded-2xl overflow-hidden border border-gray-100 shadow-sm mb-4">
          <div className="relative h-[130px] sm:h-[170px] md:h-[200px] overflow-hidden">
            <img src={ASSETS.profileCover} alt="Cover" className="w-full h-full object-cover" />
            <button
              onClick={() => setShowEdit(true)}
              className="absolute top-3 right-3 flex items-center gap-1.5 px-3 sm:px-4 py-1.5 sm:py-2 bg-[#FEDC5A] text-gray-800 rounded-full font-semibold text-[12px] sm:text-[13px] shadow hover:brightness-95 transition-all"
            >
              <svg width="13" height="13" viewBox="0 0 19 19" fill="none">
                <path d="M4 5H3C2.46957 5 1.96086 5.21071 1.58579 5.58579C1.21071 5.96086 1 6.46957 1 7V16C1 16.5304 1.21071 17.0391 1.58579 17.4142C1.96086 17.7893 2.46957 18 3 18H12C12.5304 18 13.0391 17.7893 13.4142 17.4142C13.7893 17.0391 14 16.5304 14 16V15" stroke="#1F3A60" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M13 3.00011L16 6.00011M17.385 4.58511C17.7788 4.19126 18.0001 3.65709 18.0001 3.10011C18.0001 2.54312 17.7788 2.00895 17.385 1.61511C16.9912 1.22126 16.457 1 15.9 1C15.343 1 14.8088 1.22126 14.415 1.61511L6 10.0001V13.0001H9L17.385 4.58511Z" stroke="#1F3A60" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <span>Edit Profile</span>
            </button>
          </div>

          <div className="px-5 sm:px-8 pb-5 relative">
            <div className="absolute -top-[40px] sm:-top-[100px] left-5 sm:left-8">
              <div className="w-[80px] h-[80px] sm:w-[170px] sm:h-[170px] rounded-full overflow-hidden border-[3px] sm:border-[6px] border-white shadow-lg">
                <img
                  src={getAssetUrl(p.avatar)}
                  alt={p.name}
                  className="w-full h-full object-cover"
                // onError={(e) => {
                //   e.currentTarget.src = "/assets/images/avatar-brother-julian.png";
                // }}
                />
              </div>
            </div>

            <div className="flex justify-end pt-6 mb-1">
              <div className="flex items-center gap-3">
                <img src={PremiumIcon} alt="Premium" className="" />
                <div className="flex justify-center items-center">
                  <div className="px-4 py-.5 rounded-full bg-[#D3B850]/20 border border-[#D3B850]">
                    <span className="text-[10px] sm:text-[12px] font-bold uppercase tracking-widest text-[#D3B850]">
                      {subscription.plan_name || "Member"}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-end justify-between gap-4 mt-6 sm:mt-8 flex-wrap">
              <div className="flex flex-col gap-1 min-w-0">
                <div className="flex items-end gap-2 flex-wrap">
                  <h1 className="text-[24px] sm:text-[22px] font-bold text-[#0F172A] font-body leading-tight">
                    {p?.name || "—"}
                  </h1>
                  <span className="text-[18px] font-semibold text-[#969696] whitespace-nowrap mb-[2px] capitalize">
                    • {p.user_role || "Mentor"}
                  </span>
                </div>

                <p className="text-[14px] text-gray-500 font-body">
                  Member since {p.member_since || "—"}
                  {/* {" · "}
                  {p.location || "Location not set"} */}
                </p>

                <p className="text-[16px] font-body text-[#0F172A]">
                  <span className="font-black">+{stats.followers ?? 0}</span> Followers
                </p>
              </div>

              <div className="flex flex-col items-start gap-3 flex-shrink-0">
                {p.points >= 500 && (<>
                  <p className="text-[17px] font-normal text-[#1A1A1A] font-body">Badges Achieved</p>
                  <div className="flex items-center gap-2">

                    {p.points >= 500 && (
                      <img src={Badgge1} alt="500 Points Badge" className="w-[34.69px] h-[34.69px] object-contain" />
                    )}

                    {p.points >= 1000 && (
                      <img src={Badgge2} alt="1000 Points Badge" className="w-[34.69px] h-[34.69px] object-contain" />
                    )}

                    {p.points >= 2000 && (
                      <img src={Badgge3} alt="2000 Points Badge" className="w-[34.69px] h-[34.69px] object-contain" />
                    )}

                    {p.points >= 5000 && (
                      <img src={Badgge4} alt="5000 Points Badge" className="w-[34.69px] h-[34.69px] object-contain" />
                    )}

                    {p.points >= 10000 && (
                      <img src={Badgge5} alt="10000 Points Badge" className="w-[34.69px] h-[34.69px] object-contain" />
                    )}

                    {p.points >= 20000 && (
                      <img src={Badgge6} alt="20000 Points Badge" className="w-[34.69px] h-[34.69px] object-contain" />
                    )}
                  </div></>)}
                <p className="text-[15.4px] font-normal text-[#657786] font-body">
                  Points Earned: <span className="text-[#657786]">{p.points ?? 0}</span>
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* ── CARD 2: Your Role ── */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm px-5 sm:px-8 py-5 mb-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
            <div>
              <h3 className="text-[15px] sm:text-[16px] md:text-[20px] font-medium text-[#0F172A] font-body">Your Role</h3>
              <p className="text-[14px] text-gray-500 font-body mt-0.5">
                Choose how you want to participate. You can change this anytime.
              </p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {[
                { value: "mentor", label: "Mentor" },
                { value: "mentee", label: "Mentee" },
                { value: "both", label: "Both" },
              ].map(({ value, label }) => (
                <button
                  key={value}
                  onClick={() => handleRoleChange(value)}
                  disabled={updateRoleMutation.isPending}
                  className={`px-4 sm:px-5 py-1.5 sm:py-2 rounded-full text-[12px] sm:text-[13px] font-semibold font-body transition-all border disabled:opacity-60
                    ${p.user_role === value
                      ? "bg-[#1F3A5F] text-white border-[#1F3A5F] shadow-sm"
                      : "bg-white text-gray-600 border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                    }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div className="border-t-2 border-gray-100 pt-4">
            <p className="text-[14px] text-gray-500 font-body mb-2.5">Your interest tags drive matches:</p>
            <div className="flex items-center gap-2 flex-wrap">
              {tags.length === 0 && (
                <span className="text-[13px] text-gray-400 font-body">No tags yet.</span>
              )}
              {tags.map((tag) => (
                <span
                  key={tag}
                  className="text-[12px] font-medium text-[#2b2b2b] border border-gray-200 rounded-full px-4 py-1.5 bg-gray-300 font-body capitalize"
                >
                  {tag}
                </span>
              ))}
              <button
                onClick={() => setShowEditTags(true)}
                className="ml-auto text-[16px] text-[#2b2b2b] border border-gray-200 rounded-lg px-5 py-1.5 bg-gray-100 hover:bg-gray-50 transition-all font-body whitespace-nowrap"
              >
                Add/Edit Tags
              </button>
            </div>
          </div>
        </div>



        {/* ── CARD 3: About ── */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm px-5 sm:px-8 py-5 mb-4">
          <h3 className="text-[17px] sm:text-[24px] font-bold text-[#0F172A] font-body mb-3">About</h3>
          <p className="text-[13px] sm:text-[14px] text-gray-600 leading-[1.75] font-body">
            {p.about_us || ""}
          </p>
        </div>

        {/* ── Feed Tabs ── */}
        <div className="flex items-center gap-2 sm:gap-3 mb-4">
          {[
            { key: "feed", label: "My Faith Feed" },
            { key: "saved", label: "Saved Streams" },
          ].map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={`px-4 py-2 rounded-full text-[11px] sm:text-[12px] font-bold uppercase tracking-wide transition-all font-body
                ${activeTab === key ? "bg-primary text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* ── Posts ── */}
        {activeTab === "feed" ? (
          /* ── My Posts Feed ── */
          <>
            {/* Loading State */}
            {isLoadingPosts && allPosts.length === 0 ? (
              <div className="flex justify-center py-12">
                <div className="w-8 h-8 border-3 border-primary border-t-transparent rounded-full animate-spin" />
              </div>
            ) : allPosts.length === 0 ? (
              <p className="text-gray-400 py-12 text-center">No posts yet. Create your first one!</p>
            ) : (
              <div className="space-y-6 pb-10">
                {allPosts.map((post) => (
                  <PostCard key={post.id} post={post} />
                ))}
              </div>
            )}

            {/* Load More Button */}
            {hasMore && (
              <div className="flex justify-center mt-10 mb-6">
                <button
                  onClick={loadMorePosts}
                  disabled={isFetchingMore}
                  className="px-8 py-3 bg-gray-100 hover:bg-gray-200 rounded-full font-medium text-sm transition-all disabled:opacity-70 flex items-center gap-2"
                >
                  {isFetchingMore ? (
                    <>
                      <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                      Loading more...
                    </>
                  ) : (
                    "Load More Posts"
                  )}
                </button>
              </div>
            )}
          </>
        ) : (
          /* ── Saved Streams Tab ── */
          isLoadingSaved && allSavedStreams.length === 0 ? (
            <div className="flex justify-center py-12">
              <div className="w-8 h-8 border-3 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : allSavedStreams.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-gray-400 text-lg">No saved streams yet.</p>
              <p className="text-gray-500 mt-2">Save interesting media from Faith Stream</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 pb-10">
                {allSavedStreams.map((item) => (
                  <div
                    key={item.id}
                    className="group cursor-pointer bg-white rounded-2xl overflow-hidden border border-gray-100 hover:shadow-md transition-all"
                    onClick={() => setSelectedMedia(item)}
                  >
                    <div className="relative aspect-video bg-gray-100">
                      <img
                        src={getAssetUrl(item.thumbnail)}
                        alt={item.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                      />
                      {isVideoFile(item.media_url) && (
                        <div className="absolute inset-0 flex items-center justify-center opacity-80 group-hover:opacity-100 transition-opacity">
                          <div className="w-11 h-11 bg-white/80 rounded-full flex items-center justify-center shadow-md">
                            <Icon name="play" size={18} className="text-gray-700 ml-0.5" />
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="p-4">
                      <h3 className="font-semibold text-gray-900 line-clamp-2 mb-1">{item.title}</h3>
                      <div className="flex items-center justify-between">
                        <p className="text-sm text-gray-500">{item.author}</p>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleSaveToggle(item); }}
                          className="flex items-center gap-1 text-[11px] font-bold text-black hover:underline"
                        >
                          <Icon name={"bookmarkOff"} size={14} className="text-black" />
                          Unsave
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Load More Streams */}
              {hasMoreStreams && (
                <div className="flex justify-center mt-10 mb-6">
                  <button
                    onClick={loadMoreStreams}
                    disabled={isFetchingMoreStreams}
                    className="px-8 py-3 bg-gray-100 hover:bg-gray-200 rounded-full font-medium text-sm transition-all disabled:opacity-70 flex items-center gap-2"
                  >
                    {isFetchingMoreStreams ? (
                      <>
                        <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                        Loading more...
                      </>
                    ) : (
                      "Load More Streams"
                    )}
                  </button>
                </div>
              )}
            </>
          ))}
      </div>
      {selectedMedia && (
        <MediaModal
          item={selectedMedia}
          onClose={() => setSelectedMedia(null)}
        />
      )}

      <EditProfileModal
        isOpen={showEdit}
        onClose={() => setShowEdit(false)}
        user={p}
        onSaved={(fd) => updateProfileMutation.mutate(fd)}
        isSaving={updateProfileMutation.isPending}
        error={updateProfileMutation.error?.error || updateProfileMutation.error?.message}
      />
      <CreatePostModal isOpen={showCreate} onClose={() => setShowCreate(false)} />
      <EditTagsModal
        isOpen={showEditTags}
        onClose={() => setShowEditTags(false)}
        tags={tags}
        onSave={handleTagsSaved}
        isSaving={updateInterestsMutation.isPending}
      />
    </DashboardLayout>
  );
};

export default ProfilePage;